import type { ReactNode } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { focusRing } from "./buttons";

interface ChoiceCardProps {
  selected: boolean;
  onSelect: () => void;
  icon?: ReactNode;
  title: string;
  description: string;
  /** A small line under the description, e.g. the key figures a sector gets. */
  meta?: string;
  disabled?: boolean;
}

/** One option of a single-choice group (role radio); the parent owns `role="radiogroup"`. */
export function ChoiceCard({
  selected,
  onSelect,
  icon,
  title,
  description,
  meta,
  disabled = false,
}: ChoiceCardProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "flex w-full items-start gap-4 rounded-g-xl border bg-g-bg-card p-5 text-left transition-colors duration-150 ease-out",
        selected
          ? "border-g-primary bg-g-primary-wash shadow-g-card"
          : "border-g-line hover:border-g-line-strong hover:bg-g-primary-subtle active:bg-g-primary-wash",
        "disabled:cursor-not-allowed disabled:opacity-60",
        focusRing,
      )}
    >
      {icon ? (
        <span
          className={cn(
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-g-md transition-colors",
            selected ? "bg-g-primary text-g-on-primary" : "bg-g-primary-tint text-g-primary",
          )}
          aria-hidden="true"
        >
          {icon}
        </span>
      ) : null}
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-[16px] font-bold text-g-ink-900">{title}</span>
        <span className="text-[14px] text-g-ink-500">{description}</span>
        {meta ? (
          <span className="pt-1 text-[12.5px] font-medium text-g-ink-600">{meta}</span>
        ) : null}
      </span>
      <span
        className={cn(
          "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-g-pill border transition-colors",
          selected
            ? "border-g-primary bg-g-primary text-g-on-primary"
            : "border-g-line-strong bg-g-bg-card",
        )}
        aria-hidden="true"
      >
        {selected ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
      </span>
    </button>
  );
}
